import { Fragment } from 'react';
import type { IOsasc1CustomControlData } from './Osasc1V1DiagnosticReportAddEditCustomControlFields';
import { ComorbiditiesChoice } from '../../../../../../common/enums/comorbiditiesChoice';
import { SomnologyOrganizationType } from '../../../../../../common/enums/somnologyOrganizationType';
import { calculateBmi } from '../../../../../../common/utils/bmi';
import { translateKey } from '../../../../../translate';
import { FormDivider, FormInlineGroup, FormParagraph, HeaderDivider } from '../../../../elements/common';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroupRead } from '../../../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { anamnesisFieldsKsyosSleep, anamnesisFieldsNsi } from './anamnesisFields';

interface IOsasc1V1DiagnosticReportSummaryProps {
    customControlData: IOsasc1CustomControlData;
    isOpen: boolean;
}

export function Osasc1V1DiagnosticReportSummary(props: IOsasc1V1DiagnosticReportSummaryProps) {
    const anamnesisFields =
        props.customControlData.somnologyOrganizationType !== SomnologyOrganizationType.ksyosMedicalCenter
            ? anamnesisFieldsNsi
            : anamnesisFieldsKsyosSleep;
    const fields = getFields();

    return (
        <>
            {(fields.length > 0 || props.customControlData.comorbiditeitenChoice || props.isOpen) && (
                <HeaderDivider text={translateKey('carePlan.anamnese')} />
            )}

            {fields}

            {!props.isOpen &&
                props.customControlData.somnologyOrganizationType === SomnologyOrganizationType.ksyosMedicalCenter && (
                    <>
                        <HorizontalFormGroupRead>
                            <FormLabel>Comorbiditeiten</FormLabel>
                            <InputContainer>
                                <FormInlineGroup value={props.customControlData.comorbiditeitenChoice} />
                                {props.customControlData.comorbiditeitenChoice === ComorbiditiesChoice.ja && (
                                    <InputContainer>
                                        {props.customControlData.comorbiditeiten.hoge_bloeddruk && (
                                            <FormInlineGroup value="Hoge bloeddruk" />
                                        )}
                                        {props.customControlData.comorbiditeiten.cardiale_problematiek && (
                                            <FormInlineGroup value="Cardiale problematiek" />
                                        )}
                                        {props.customControlData.comorbiditeiten.diabetes_mellitus && (
                                            <FormInlineGroup value="Diabetes mellitus" />
                                        )}
                                        {props.customControlData.comorbiditeiten.burn_out_of_depressieve_klachten && (
                                            <FormInlineGroup value="Burn-out of depressieve klachten" />
                                        )}
                                        {props.customControlData.comorbiditeiten.anders_text && (
                                            <FormInlineGroup
                                                value={props.customControlData.comorbiditeiten.anders_text}
                                            />
                                        )}
                                    </InputContainer>
                                )}
                            </InputContainer>
                        </HorizontalFormGroupRead>

                        {(props.customControlData.tonsilSize ||
                            props.customControlData.tongueSize ||
                            props.customControlData.retrognathism) && (
                            <>
                                <FormDivider />
                                <HeaderDivider text={translateKey('physicalExamination.physicalExamination')} />
                            </>
                        )}

                        {props.customControlData.tonsilSize && (
                            <HorizontalFormGroupRead>
                                <FormLabel>{translateKey('physicalExamination.tonsilSize')}</FormLabel>
                                <InputContainer>
                                    <FormParagraph>{props.customControlData.tonsilSize}</FormParagraph>
                                </InputContainer>
                            </HorizontalFormGroupRead>
                        )}

                        {props.customControlData.tongueSize && (
                            <HorizontalFormGroupRead>
                                <FormLabel>{translateKey('physicalExamination.tongueSize')}</FormLabel>
                                <InputContainer>
                                    <FormParagraph>{props.customControlData.tongueSize}</FormParagraph>
                                </InputContainer>
                            </HorizontalFormGroupRead>
                        )}

                        {props.customControlData.retrognathism && (
                            <HorizontalFormGroupRead>
                                <FormLabel>{translateKey('physicalExamination.retrognathism')}</FormLabel>
                                <InputContainer>
                                    <FormParagraph>{props.customControlData.retrognathism}</FormParagraph>
                                </InputContainer>
                            </HorizontalFormGroupRead>
                        )}

                        {props.customControlData.dentitie_goed_gesaneerd_value && (
                            <>
                                <HorizontalFormGroupRead>
                                    <FormLabel>Dentitie goed gesaneerd</FormLabel>
                                    <InputContainer>
                                        <FormParagraph>
                                            {props.customControlData.dentitie_goed_gesaneerd_value}
                                        </FormParagraph>
                                    </InputContainer>
                                </HorizontalFormGroupRead>

                                {props.customControlData.dentitie_goed_gesaneerd_remark && (
                                    <HorizontalFormGroupRead>
                                        <FormLabel> {} </FormLabel>
                                        <InputContainer>
                                            <FormParagraph>
                                                {props.customControlData.dentitie_goed_gesaneerd_remark}
                                            </FormParagraph>
                                        </InputContainer>
                                    </HorizontalFormGroupRead>
                                )}
                            </>
                        )}

                        {props.customControlData.gebitsprothese_value && (
                            <HorizontalFormGroupRead>
                                <FormLabel>Gebitsprothese</FormLabel>
                                <InputContainer>
                                    <FormParagraph>{props.customControlData.gebitsprothese_value}</FormParagraph>
                                </InputContainer>
                            </HorizontalFormGroupRead>
                        )}

                        {props.customControlData.lengte !== null && props.customControlData.gewicht !== null && (
                            <HorizontalFormGroupRead>
                                <FormLabel>BMI</FormLabel>
                                <InputContainer>
                                    <FormParagraph>
                                        {calculateBmi(props.customControlData.gewicht, props.customControlData.lengte)}
                                    </FormParagraph>
                                </InputContainer>
                            </HorizontalFormGroupRead>
                        )}
                    </>
                )}
        </>
    );

    function getFields() {
        const jaFields: JSX.Element[] = [];
        const onbekendFields: JSX.Element[] = [];
        const neeWithRemarkFields: JSX.Element[] = [];

        for (const field of anamnesisFields) {
            const valueKey = `${field.key}_value` as keyof IOsasc1CustomControlData;
            const remarkKey = `${field.key}_remark` as keyof IOsasc1CustomControlData;

            if (props.customControlData[valueKey] === 'Ja') {
                jaFields.push(getAnamnesisReadElement(field.label, valueKey, remarkKey));
            } else if (props.customControlData[valueKey] === 'Onbekend') {
                onbekendFields.push(getAnamnesisReadElement(field.label, valueKey, remarkKey));
            } else if (props.customControlData[remarkKey] !== null) {
                neeWithRemarkFields.push(getAnamnesisReadElement(field.label, valueKey, remarkKey));
            }
        }

        return jaFields.concat(onbekendFields).concat(neeWithRemarkFields);
    }

    function getAnamnesisReadElement(
        label: string | null,
        valueKey: keyof IOsasc1CustomControlData,
        remarkKey: keyof IOsasc1CustomControlData
    ) {
        const value = props.customControlData[valueKey];
        const remark = props.customControlData[remarkKey];

        const displayValue = value ? value.toString() : null;

        return (
            <Fragment key={valueKey + remarkKey}>
                <HorizontalFormGroupRead>
                    <FormLabel>{label}</FormLabel>
                    <InputContainer>
                        <FormParagraph>{displayValue}</FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>

                {remark && (
                    <HorizontalFormGroupRead>
                        <FormLabel> {} </FormLabel>
                        <InputContainer>
                            <FormParagraph>{remark.toString()}</FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}
            </Fragment>
        );
    }
}
